/**
 * api.js — Updated
 *
 * Added:
 *   invoiceAPI   — full invoice module endpoints
 *   inventoryAPI — search, barcode, low-stock, valuation (new endpoints)
 *
 * PASTE AT: frontend/src/services/api.js  (REPLACE existing file)
 */

import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = 'Bearer ' + token;
  return cfg;
});

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(err.response?.data || { message: err.message });
  }
);

const make = (base) => ({
  getAll:  (params)   => api.get(base,             { params }),
  getById: (id)       => api.get(base + '/' + id),
  create:  (data)     => api.post(base, data),
  update:  (id, data) => api.put(base + '/' + id, data),
  delete:  (id)       => api.delete(base + '/' + id),
});

export const authAPI = {
  login:          (data) => api.post('/auth/login', data),
  getMe:          ()     => api.get('/auth/me'),
  register:       (data) => api.post('/auth/register', data),
  changePassword: (data) => api.put('/auth/change-password', data),
  updateProfile:  (data) => api.put('/auth/profile', data),
};

export const usersAPI    = { ...make('/users'),    getStats:    () => api.get('/users/stats') };
export const projectsAPI = { ...make('/projects'), getStats:    () => api.get('/projects/stats') };
export const tasksAPI    = { ...make('/tasks'),    getStats:    () => api.get('/tasks/stats'), addComment: (id, text) => api.post('/tasks/' + id + '/comments', { text }) };
export const calendarAPI = make('/calendar');
export const contactsAPI = { ...make('/crm'),      getStats:    () => api.get('/crm/stats') };

// ── Inventory — extended ──────────────────────────────────────
export const inventoryAPI = {
  ...make('/inventory'),
  getStats:      ()          => api.get('/inventory/stats'),
  adjustStock:   (id, data)  => api.post('/inventory/' + id + '/adjust', data),
  getTransactions:(id)       => api.get('/inventory/' + id + '/transactions'),
  // New endpoints
  search:        (q, limit)  => api.get('/inventory/search',  { params: { q, limit } }),
  findByBarcode: (code)      => api.get('/inventory/barcode/' + code),
  getLowStock:   ()          => api.get('/inventory/low-stock'),
  getValuation:  ()          => api.get('/inventory/valuation'),
  getGstSlab:    (id)        => api.get('/inventory/' + id + '/gst-slab'),
};

// ── Invoice module — full ─────────────────────────────────────
export const invoiceAPI = {
  // Core CRUD
  getAll:    (params)         => api.get('/invoices',               { params }),
  getById:   (id)             => api.get('/invoices/' + id),
  create:    (data)           => api.post('/invoices', data),
  update:    (id, data)       => api.put('/invoices/' + id, data),
  delete:    (id)             => api.delete('/invoices/' + id),

  // Status actions
  cancel:    (id, reason)     => api.post('/invoices/' + id + '/cancel',    { reason }),
  void:      (id, reason)     => api.post('/invoices/' + id + '/void',      { reason }),
  duplicate: (id)             => api.post('/invoices/' + id + '/duplicate'),
  convert:   (id, targetType) => api.post('/invoices/' + id + '/convert',   { targetType }),

  // Payments
  addPayment:    (id, data)   => api.post('/invoices/' + id + '/payments',  data),
  deletePayment: (id, pid)    => api.delete('/invoices/' + id + '/payments/' + pid),

  // PDF
getPdfUrl:     (id, tpl) => `/api/invoices/${id}/pdf${tpl ? '?template=' + tpl : ''}`,
getPreviewUrl: (id, tpl) => `/api/invoices/${id}/pdf/preview${tpl ? '?template=' + tpl : ''}`,

  // Reports & lookup
  getDashboard:  (params)     => api.get('/invoices/dashboard',           { params }),
  getHsnSummary: (id)         => api.get('/invoices/' + id + '/hsn-summary'),

  // Lookup (for invoice form)
  searchCustomers: (q)        => api.get('/invoices/lookup/customers',         { params: { q } }),
  searchProducts:  (q)        => api.get('/invoices/lookup/products',          { params: { q } }),
  getProductBarcode:(code)    => api.get('/invoices/lookup/products/barcode/' + code),
  getTaxes:        ()         => api.get('/invoices/lookup/taxes'),
  getPaymentTerms: ()         => api.get('/invoices/lookup/payment-terms'),
  getBankAccounts: ()         => api.get('/invoices/lookup/bank-accounts'),
  getSequencePreview:(type)   => api.get('/invoices/lookup/sequence-preview',  { params: { type } }),

  // Reports
  reportSales:       (p)      => api.get('/invoices/reports/sales',        { params: p }),
  reportGst:         (p)      => api.get('/invoices/reports/gst',          { params: p }),
  reportHsn:         (p)      => api.get('/invoices/reports/hsn',          { params: p }),
  reportOutstanding: (p)      => api.get('/invoices/reports/outstanding',  { params: p }),
  reportCollection:  (p)      => api.get('/invoices/reports/collection',   { params: p }),
  reportMonthly:     (p)      => api.get('/invoices/reports/monthly',      { params: p }),
  reportTopCustomers:(p)      => api.get('/invoices/reports/top-customers',{ params: p }),
  reportTopProducts: (p)      => api.get('/invoices/reports/top-products', { params: p }),
};

// ── Legacy accounting (keep for existing pages) ───────────────
export const accountingAPI = {
  ...make('/accounting'),
  getSummary:    (params) => api.get('/accounting/summary',        { params }),
  getLedger:     (cid)    => api.get('/accounting/ledger/'       + cid),
  getLedgerFull: (cid, p) => api.get('/accounting/ledger-full/'  + cid, { params: p }),
  getProfitLoss: (params) => api.get('/accounting/reports/pl',    { params }),
  getGSTSummary: (params) => api.get('/accounting/reports/gst',   { params }),
  convert:       (id, t)  => api.post('/accounting/' + id + '/convert', { targetType: t }),
};

export const dataEntryAPI    = make('/dataentry');
export const companyAPI      = { get: () => api.get('/company'), update: (data) => api.put('/company', data) };
export const projectCostsAPI = {
  getAll:     (pid, p)     => api.get('/projects/' + pid + '/costs',          { params: p }),
  getSummary: (pid)        => api.get('/projects/' + pid + '/costs/summary'),
  create:     (pid, data)  => api.post('/projects/' + pid + '/costs', data),
  update:     (pid, id, d) => api.put('/projects/' + pid + '/costs/' + id, d),
  delete:     (pid, id)    => api.delete('/projects/' + pid + '/costs/' + id),
};
export const projectDocumentsAPI = {
  getByStage: (pid, stage) => api.get('/projects/' + pid + '/documents', { params: { stage } }),
  getAll:     (pid)        => api.get('/projects/' + pid + '/documents'),
  upload:     (pid, formData) => api.post('/projects/' + pid + '/documents', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  remove:     (pid, docId) => api.delete('/projects/' + pid + '/documents/' + docId),
};
export const customFieldsAPI = {
  getAll: (p) => api.get('/custom-fields', { params: p }),
  create: (d) => api.post('/custom-fields', d),
  update: (id, d) => api.put(`/custom-fields/${id}`, d),
  remove: (id)    => api.delete(`/custom-fields/${id}`),
};

export default api;